import {inject, Injectable} from '@angular/core';
import {Meta, Title} from '@angular/platform-browser';
import {ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy} from '@angular/router';
import {APP_SIGNALS} from '@app/core/tokens/app-signals.token';
import {stripHtmlTags} from '@utilities/clean-up-html';

@Injectable({providedIn: 'root'})
export class CustomTitleStrategy extends TitleStrategy {
  private readonly titleService = inject(Title);
  private readonly metaService = inject(Meta);
  private readonly instanceName = inject(APP_SIGNALS).instanceName;

  override updateTitle(routerState: RouterStateSnapshot): void {
    const snapshot = this.deepestSnapshot(routerState.root);
    const {pageTitle, metaDescription} = this.resolvePageMeta(snapshot);

    const instanceNameValue = this.instanceName();
    const fullTitle = pageTitle ? `${instanceNameValue} | ${pageTitle}` : instanceNameValue;

    this.titleService.setTitle(fullTitle);
    this.metaService.updateTag({name: 'title', content: fullTitle});
    this.metaService.updateTag({property: 'og:title', content: fullTitle});

    if (metaDescription !== undefined) {
      this.metaService.updateTag({name: 'description', content: metaDescription});
      this.metaService.updateTag({property: 'og:description', content: metaDescription});
    }
  }

  override getResolvedTitleForRoute(snapshot: ActivatedRouteSnapshot): string | undefined {
    return this.resolvePageMeta(snapshot).pageTitle;
  }

  private resolvePageMeta(snapshot: ActivatedRouteSnapshot): {pageTitle?: string; metaDescription?: string} {
    // Static routes: pageMeta object in route data
    if (snapshot.data['pageMeta']) {
      return snapshot.data['pageMeta'];
    }

    // Dynamic routes: resolver data
    const resource = snapshot.data['resource'];
    if (resource) {
      return {
        pageTitle: resource.name,
        metaDescription: resource.description ? stripHtmlTags(resource.description).slice(0, 155) : undefined,
      };
    }

    const projectData = snapshot.data['projectData'];
    if (projectData?.project) {
      return {
        pageTitle: projectData.project.name,
        metaDescription: projectData.project.description
          ? stripHtmlTags(projectData.project.description).slice(0, 155)
          : undefined,
      };
    }

    const dataset = snapshot.data['dataset'];
    if (dataset) {
      return {
        pageTitle: dataset.name,
        metaDescription: dataset.description ? stripHtmlTags(dataset.description).slice(0, 155) : undefined,
      };
    }

    // Home page: intro title from resolver
    const homePageContent = snapshot.data['homePageContent'];
    if (homePageContent !== undefined) {
      return {
        pageTitle: 'Home Page',
        metaDescription: homePageContent?.intro?.title,
      };
    }

    return {};
  }

  private deepestSnapshot(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    let current = route;
    while (current.firstChild) {
      current = current.firstChild;
    }
    return current;
  }
}
